import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Search, ChevronDown, Sparkles, ArrowLeft, Rocket, Palette, Users,
  ShieldCheck, Share2, QrCode, TrendingUp, DollarSign, Lock, HelpCircle,
} from "lucide-react";

/**
 * /visual-studio-faq — public FAQ for the AI Visual Campaign Studio.
 *
 * Content is grouped into 12 sections. Each Q/A is a collapsible accordion.
 * A search box filters across every Q + A in real time.
 */
export default function VisualStudioFaq() {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return SECTIONS;
    return SECTIONS.map(sec => ({
      ...sec,
      items: sec.items.filter(it =>
        (it.q + " " + it.a).toLowerCase().includes(s)),
    })).filter(sec => sec.items.length > 0);
  }, [q]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-fuchsia-950 text-white pb-20"
         data-testid="visual-studio-faq">
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none mix-blend-overlay"
           style={{ backgroundImage:
             "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80'><filter id='n'><feTurbulence baseFrequency='0.9'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")" }} />

      <div className="relative max-w-4xl mx-auto px-6 pt-14">
        <Link to="/ad-builder"
              className="text-white/50 hover:text-white text-xs inline-flex items-center gap-1"
              data-testid="faq-back-link">
          <ArrowLeft className="w-3 h-3" /> Back to Ad Builder
        </Link>
        <div className="mt-3 text-[10px] uppercase tracking-[0.35em] text-fuchsia-400 font-bold">
          WebsiteTech · AI Visual Campaign Studio
        </div>
        <h1 className="mt-3 text-4xl sm:text-5xl font-bold leading-tight">
          Frequently Asked{" "}
          <span className="bg-gradient-to-r from-fuchsia-400 via-orange-300 to-amber-300 bg-clip-text text-transparent">
            Questions
          </span>
        </h1>
        <p className="mt-4 text-white/70 max-w-2xl">
          Everything you need to know about how the Campaign Studio analyzes
          your website, generates ads, tracks performance, and improves over
          time — with your approval at every step.
        </p>

        {/* Analyze → Create → Learn banner */}
        <div className="mt-8 rounded-2xl bg-white/5 ring-1 ring-white/10 backdrop-blur p-5"
             data-testid="faq-workflow-banner">
          <div className="flex flex-wrap items-center gap-2 text-sm font-bold">
            {["Analyze", "Create", "Review", "Approve", "Distribute", "Measure", "Learn", "Improve"].map((s, i, arr) => (
              <span key={s} className="inline-flex items-center gap-2">
                <span className="rounded-full bg-fuchsia-500/20 text-fuchsia-200 px-3 py-1 text-xs">
                  {s}
                </span>
                {i < arr.length - 1 && <span className="text-white/30">→</span>}
              </span>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="mt-6 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search 50+ questions…"
                  data-testid="faq-search"
                  className="w-full rounded-xl bg-white/10 ring-1 ring-white/10 pl-9 pr-3 py-3 text-sm placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-fuchsia-400" />
        </div>

        {/* Sections */}
        <div className="mt-8 space-y-8">
          {filtered.length === 0 && (
            <div className="text-center text-white/50 py-10">
              No matches. Try a broader term.
            </div>
          )}
          {filtered.map((sec) => (
            <FaqSection key={sec.id} section={sec} />
          ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-16 rounded-3xl bg-gradient-to-r from-fuchsia-500/20 via-orange-400/10 to-amber-400/10 ring-1 ring-fuchsia-400/30 backdrop-blur p-6 flex flex-wrap items-center justify-between gap-4"
             data-testid="faq-cta">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold">
              Ready to try it
            </div>
            <div className="text-lg font-bold">Free campaign preview — no credit card</div>
            <div className="text-sm text-white/60">
              Enter a website. Get 3 concepts, 4 formats, a short URL, and a QR code in 60 seconds.
            </div>
          </div>
          <Link to="/ad-builder"
                 data-testid="faq-cta-btn"
                 className="rounded-xl bg-gradient-to-r from-fuchsia-500 to-orange-400 hover:brightness-110 text-white px-5 py-3 font-bold inline-flex items-center gap-2">
            Build my ad <Rocket className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}


function FaqSection({ section }) {
  const Icon = section.icon;
  return (
    <section className="rounded-2xl bg-white/5 ring-1 ring-white/10 backdrop-blur p-6"
             data-testid={`faq-section-${section.id}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-xl bg-fuchsia-500/20 flex items-center justify-center">
          <Icon className="w-4 h-4 text-fuchsia-300" />
        </div>
        <h2 className="text-xl font-bold">{section.title}</h2>
      </div>
      <div className="space-y-2">
        {section.items.map((it, i) => (
          <FaqItem key={`${section.id}-${i}`} q={it.q} a={it.a}
                    testid={`faq-item-${section.id}-${i}`} />
        ))}
      </div>
    </section>
  );
}

function FaqItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl bg-white/[0.03] ring-1 ring-white/5"
         data-testid={testid}>
      <button onClick={() => setOpen(!open)}
              data-testid={`${testid}-toggle`}
              className="w-full flex items-start justify-between gap-4 text-left p-4 hover:bg-white/[0.04] rounded-xl">
        <span className="font-bold text-sm sm:text-base">{q}</span>
        <ChevronDown className={`w-4 h-4 mt-1 shrink-0 text-white/50 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 -mt-1 text-sm text-white/75 whitespace-pre-line leading-relaxed"
             data-testid={`${testid}-answer`}>
          {a}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
//  FAQ content
// ---------------------------------------------------------------------------
const SECTIONS = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: Sparkles,
    items: [
      { q: "What is WebsiteTech AI Campaign Studio?",
        a: "WebsiteTech AI Campaign Studio is an AI-assisted system for creating, managing, distributing, tracking, and improving advertising campaigns.\n\nInstead of using separate tools for graphic design, advertising copy, resizing, links, QR codes, campaign tracking, and analytics, WebsiteTech brings these functions together into one workflow:\n\nAnalyze → Create → Review → Approve → Distribute → Measure → Learn → Improve" },
      { q: "What do I need to get started?",
        a: "For many businesses, you can start with just your website address. WebsiteTech can analyze your existing site to identify:\n\n• Business name\n• Products and services\n• Logo\n• Brand colors\n• Existing photographs\n• Geographic market\n• Important website pages\n• Calls to action\n• Contact information\n• Existing promotions\n\nYou can then review and correct this information before creating a campaign. If you don't have a website or your website does not contain enough information, you can enter your business information manually." },
      { q: "Can WebsiteTech create advertisements from my existing website?",
        a: "Yes. Enter your website address and WebsiteTech uses your existing content and branding as a starting point for campaign creation. The system suggests campaign ideas, headlines, calls to action, audiences, images, ad copy and layouts. You remain in control of what is approved." },
      { q: "Do I need advertising experience?",
        a: "No. WebsiteTech is designed so business owners don't need to understand complicated advertising terminology. The AI can recommend campaign objectives, audiences, creative concepts, formats and calls to action. You review its recommendations before deciding what to use." },
      { q: "Do I need a graphic designer?",
        a: "Not necessarily. WebsiteTech can generate complete advertising graphics and adapt them to multiple formats. Businesses that already have designers or marketing agencies can also use WebsiteTech as a collaboration and campaign-management system." },
    ],
  },
  {
    id: "ai-campaign",
    title: "AI Campaign Creation",
    icon: Rocket,
    items: [
      { q: "How does WebsiteTech create a campaign?",
        a: "You can begin with your website, product, service, event, article, promotion or other campaign objective. WebsiteTech then:\n\n1. Analyzes your business\n2. Reviews your Brand Brain\n3. Suggests an audience\n4. Recommends campaign objectives\n5. Creates several campaign concepts\n6. Generates headlines and advertising copy\n7. Generates visual advertisements\n8. Produces different advertising sizes\n9. Creates Splink.pro tracking links\n10. Generates QR codes where appropriate\n11. Presents the campaign for review and approval\n\nNothing publishes simply because AI generated it." },
      { q: "Can WebsiteTech suggest campaign ideas?",
        a: "Yes. For example, a local business could ask:\n\"Help me get more appointments next month.\"\n\nWebsiteTech will suggest several different campaign concepts and explain the purpose of each. You select the direction you prefer before proceeding." },
      { q: "Can I create a campaign from a product, article, event or promotion instead of my whole website?",
        a: "Yes. Campaign Studio works with individual products, services, events, articles, offers, announcements, landing pages, destinations, or business locations. You choose exactly what to promote." },
    ],
  },
  {
    id: "creative-graphics",
    title: "Creative & Graphics",
    icon: Palette,
    items: [
      { q: "Does WebsiteTech generate the advertising graphics?",
        a: "Yes. WebsiteTech combines AI-generated graphics, your own photographs, existing website images, approved stock imagery, and your brand assets to create campaign graphics." },
      { q: "Can I upload my own photographs?",
        a: "Yes. You can upload product photos, business photos, staff images, event pictures and other authorized images. You select which images WebsiteTech should use." },
      { q: "Can I edit AI-generated advertisements?",
        a: "Yes. Campaign creatives remain editable before approval. Depending on the template, you can change:\n\n• Headline\n• Description\n• Image\n• Background\n• CTA\n• Logo\n• Colors\n• Fonts\n• Layout\n• Contact information\n• QR code\n\nYou can also ask AI to create another variation." },
      { q: "Can WebsiteTech create several advertising designs for me to choose from?",
        a: "Yes. Rather than producing only one advertisement, Campaign Studio generates multiple concepts. For example:\n\n• Concept A — Product focused\n• Concept B — Customer-benefit focused\n• Concept C — Educational or trust focused\n\nYou choose, edit or regenerate the concepts." },
      { q: "What advertising sizes can WebsiteTech create?",
        a: "WebsiteTech supports common digital and social advertising formats:\n\n• Square social graphics\n• Portrait social graphics\n• Vertical Story/Reel graphics\n• Landscape graphics\n• Website banners\n• Desktop advertisements\n• Mobile strip advertisements\n• Sidebar advertisements\n• Email graphics\n• SocialPulse Network advertisements\n• QR promotional graphics\n• Printable promotional materials (5×7 flyers, business cards, posters)\n\nSupported formats update as ad-platform requirements change." },
      { q: "Can one campaign automatically create Facebook, Instagram, Stories, website banners and mobile advertisements?",
        a: "Yes. This is one of the central features of Campaign Studio. Create the campaign once and WebsiteTech adapts the approved campaign into multiple destination formats — intelligently rearranging content rather than simply cropping the same picture." },
      { q: "Who owns the advertising graphics?",
        a: "Ownership and permitted use depend on the source materials and the WebsiteTech service terms. Customers retain appropriate rights to their own uploaded logos, photographs and brand assets. AI-generated or licensed assets may be subject to provider or licensing terms. WebsiteTech clearly identifies relevant restrictions where applicable." },
    ],
  },
  {
    id: "brand-brain",
    title: "Brand Brain",
    icon: Sparkles,
    items: [
      { q: "What is WebsiteTech Brand Brain?",
        a: "Brand Brain is the persistent brand profile WebsiteTech builds for your business. It remembers:\n\n• Logo\n• Brand colors\n• Fonts\n• Business description\n• Products and services\n• Brand voice\n• Preferred image styles\n• Calls to action\n• Approved terminology\n• Required disclaimers\n• Prohibited terminology\n• Previously approved campaigns\n\nThis keeps future campaigns consistent with your business." },
      { q: "Can I lock my logo and brand colors?",
        a: "Yes. Important brand elements can be marked as locked:\n\n• Logo — LOCKED\n• Business Name — LOCKED\n• Primary Colors — LOCKED\n• Required Disclaimer — LOCKED\n\nAI cannot change locked brand elements without authorization." },
      { q: "Can WebsiteTech remember words that we must or must not use?",
        a: "Yes. Brand Brain can contain approved and prohibited terminology. Particularly useful for businesses that require consistent product descriptions, legal language, disclaimers or brand terminology." },
    ],
  },
  {
    id: "audience",
    title: "Audience Brain",
    icon: Users,
    items: [
      { q: "What is Audience Brain?",
        a: "Audience Brain helps WebsiteTech understand the types of customers a campaign is intended to reach. It considers geography, customer needs, business category, purchase intent, interests, language and previous campaign performance." },
      { q: "Does WebsiteTech automatically decide who should see my advertisement?",
        a: "WebsiteTech recommends audiences. You can review and modify those recommendations before a campaign is approved. Advertising-platform rules also determine what targeting options are available." },
    ],
  },
  {
    id: "approval",
    title: "Approval & Team Controls",
    icon: ShieldCheck,
    items: [
      { q: "Does AI automatically publish my advertisements?",
        a: "No. WebsiteTech follows an AI-assisted, human-controlled approach. AI can analyze, recommend, write, design and prepare campaigns. Publishing or spending advertising money requires appropriate authorization." },
      { q: "Can employees prepare advertisements without being allowed to publish them?",
        a: "Yes. Campaign Studio supports approval workflows:\n\nMarketing Employee → Creates Campaign\n↓\nManager → Reviews\n↓\nManager → Requests Changes or Approves\n↓\nAuthorized User → Publishes\n\nParticularly useful for organizations with multiple staff or outside agencies." },
      { q: "Can I save a campaign without publishing it?",
        a: "Yes. Campaigns can be saved as drafts for later review, editing or approval." },
    ],
  },
  {
    id: "advertising",
    title: "Advertising & Distribution",
    icon: Share2,
    items: [
      { q: "Can WebsiteTech run Meta/Facebook and Instagram advertising?",
        a: "WebsiteTech is designed to support connections with major advertising platforms. Availability depends on the integrations enabled for your WebsiteTech account and the requirements of each advertising provider. Campaign Studio can also prepare appropriately formatted assets for manual export where direct publishing is unavailable." },
      { q: "Can WebsiteTech run Google advertising?",
        a: "Google advertising is planned as part of the external advertising connector architecture. Availability and supported campaign types may vary." },
      { q: "What is SocialPulse Network advertising?",
        a: "WebsiteTech supports advertising across participating websites in the SocialPulse network. This creates campaigns targeted by relevant geography, portal, category or content — for example, appearing on participating geographic, travel, agriculture, food or community portals. Availability depends on participating sites and available advertising inventory." },
      { q: "Do I control my advertising budget?",
        a: "Yes. Customers are shown the proposed campaign budget before a paid campaign is launched. WebsiteTech does not increase or initiate paid advertising expenditure beyond authorized controls." },
      { q: "Can I pause an advertising campaign?",
        a: "For supported advertising integrations, Campaign Studio provides campaign-management controls such as pausing. Actual platform behavior may depend on the external advertising provider." },
      { q: "Does WebsiteTech charge an advertising management fee?",
        a: "Any WebsiteTech service, management, platform or media fees are displayed clearly before purchase or campaign approval. Advertising spend paid to third-party networks is distinguishable from WebsiteTech fees. There are no hidden advertising-management charges." },
    ],
  },
  {
    id: "splink",
    title: "Splink.pro Tracking",
    icon: TrendingUp,
    items: [
      { q: "What is Splink.pro?",
        a: "Splink.pro is the short-link and tracking technology integrated with the WebsiteTech advertising system. Instead of using one identical link everywhere, Campaign Studio creates separate trackable links for different channels and placements." },
      { q: "What can Splink.pro track?",
        a: "Depending on the campaign configuration and privacy settings, Splink.pro can help identify:\n\n• Campaign\n• Advertising creative\n• Source\n• Placement\n• Portal\n• Click\n• QR scan\n• Destination page\n• Time of interaction\n\nWhere additional conversion tracking is configured, WebsiteTech may also connect campaign activity with leads or conversions." },
      { q: "Why use different Splink links for different advertisements?",
        a: "It helps determine where responses originated. For example:\n\n• Facebook → Splink A\n• Instagram → Splink B\n• SocialPulse → Splink C\n• Email → Splink D\n• Printed QR Code → Splink E\n\nCampaign Studio can then compare their performance." },
    ],
  },
  {
    id: "qr",
    title: "QR Codes",
    icon: QrCode,
    items: [
      { q: "Can WebsiteTech automatically create a QR code for my campaign?",
        a: "Yes. Campaign Studio creates a QR code connected to a campaign-specific Splink.pro URL." },
      { q: "Where can I use the QR code?",
        a: "QR codes are useful on:\n\n• Flyers\n• Posters\n• Brochures\n• Store displays\n• Event materials\n• Business cards\n• Menus\n• Farm signs\n• Tourism materials\n• Print advertising\n\nA customer scans the code and is directed to the campaign destination." },
      { q: "Can WebsiteTech tell me how many people scanned my QR code?",
        a: "Campaign-specific QR codes are measured through Splink.pro tracking, subject to the tracking configuration used. This allows QR responses to be compared with other campaign channels." },
    ],
  },
  {
    id: "leads",
    title: "Leads & Conversions",
    icon: TrendingUp,
    items: [
      { q: "Does WebsiteTech measure more than clicks?",
        a: "That is the goal. Clicks alone don't necessarily tell a business whether advertising is successful. Where integrations are available, Campaign Intelligence follows the customer journey further:\n\nAdvertisement → Click/QR → Landing Page → Lead → Appointment/Order → Conversion" },
      { q: "Can WebsiteTech tell me which advertisement generated a customer?",
        a: "When sufficient tracking information is available, WebsiteTech can associate campaign interactions with downstream leads and conversions. However, WebsiteTech distinguishes between known attribution and assumptions — it does not claim that an advertisement caused a sale simply because a customer previously clicked it." },
    ],
  },
  {
    id: "intelligence",
    title: "Campaign Intelligence",
    icon: Sparkles,
    items: [
      { q: "What is WebsiteTech Campaign Intelligence?",
        a: "Campaign Intelligence is WebsiteTech's advertising-learning system. Instead of forgetting what happened after a campaign ends, WebsiteTech maintains a history of campaign creatives, audiences, placements and available performance information. This becomes a Campaign Memory for your business." },
      { q: "Does WebsiteTech learn from previous campaigns?",
        a: "Yes, where sufficient campaign data is available. WebsiteTech may identify patterns involving:\n\n• Images\n• Headlines\n• Calls to action\n• Audiences\n• Locations\n• Advertising formats\n• Placements\n• Landing pages\n\nThese findings inform future campaign recommendations." },
      { q: "Can I ask AI questions about my advertising results?",
        a: "Yes. Campaign Intelligence supports natural-language questions:\n\n• \"Which campaigns generated the most leads?\"\n• \"Which images performed best?\"\n• \"Which headline had the highest click-through rate?\"\n• \"Which geographic areas responded?\"\n• \"Which advertisements generated appointments?\"\n• \"Show me my best-performing campaign creatives.\"" },
      { q: "Can WebsiteTech explain why one advertisement performed better?",
        a: "WebsiteTech analyzes available campaign information and identifies plausible performance differences. However, it distinguishes measured facts from hypotheses. For example:\n\n• Measured: Creative A had a higher click-through rate.\n• Possible explanation: Creative A used a clearer offer.\n\nWebsiteTech does not present an untested explanation as proven fact." },
      { q: "Can WebsiteTech create a new campaign based on previous successful campaigns?",
        a: "Yes. You may eventually be able to ask:\n\n\"Create a new campaign using what we learned from my successful campaigns.\"\n\nCampaign Studio uses relevant Campaign Intelligence while still generating new creative options for your approval." },
    ],
  },
  {
    id: "privacy",
    title: "Privacy & Security",
    icon: Lock,
    items: [
      { q: "What customer information does WebsiteTech collect?",
        a: "The information collected depends on the WebsiteTech services you use. It may include information necessary for:\n\n• Account management\n• Brand profiles\n• Campaign creation\n• Uploaded assets\n• Campaign approvals\n• Advertising integrations\n• Analytics\n• Lead tracking\n• Billing\n\nWebsiteTech collects only information reasonably necessary to provide the requested services." },
      { q: "Is my Campaign Intelligence shared with other businesses?",
        a: "A customer's private campaign history is not exposed to another customer. If WebsiteTech develops broader campaign-learning models, information is appropriately aggregated, anonymized or otherwise handled according to applicable permissions, agreements and privacy requirements." },
      { q: "What information is sent to Meta, Google or another advertising provider?",
        a: "Only information required for the particular integration and authorized campaign is shared, subject to the advertising provider's requirements and the customer's configuration. WebsiteTech clearly identifies third-party services involved in campaign delivery and measurement." },
      { q: "How long does WebsiteTech keep my campaign data?",
        a: "Retention periods are documented in WebsiteTech's privacy and data-retention policies. Where appropriate, customers have controls for managing campaign information subject to legal, security, billing and operational retention requirements." },
    ],
  },
  {
    id: "pricing",
    title: "Pricing",
    icon: DollarSign,
    items: [
      { q: "How much does WebsiteTech Campaign Studio cost?",
        a: "WebsiteTech supports different service levels rather than requiring every business to purchase every capability. Possible offerings include:\n\n• Free campaign preview\n• Campaign creation\n• Creative-generation packages\n• WebsiteTech subscription plans\n• Campaign tracking\n• Campaign Intelligence\n• Managed advertising services\n• SocialPulse Network advertising\n• Optional third-party paid advertising\n\nCurrent prices and included services are always displayed before purchase." },
      { q: "Is advertising spend included in my WebsiteTech subscription?",
        a: "Not necessarily. WebsiteTech service fees, SocialPulse advertising and third-party advertising spend are identified separately whenever applicable. This makes it easier to understand where your advertising budget is going." },
      { q: "Can I try Campaign Studio before paying for advertising?",
        a: "Yes. WebsiteTech offers a Free Campaign Preview. Enter your website and receive:\n\n• Business Analysis\n• Suggested Audience\n• Campaign Idea\n• Sample Headline\n• Sample Advertisement\n\nYou then decide whether to create or launch the complete campaign." },
    ],
  },
  {
    id: "results",
    title: "Results & Expectations",
    icon: TrendingUp,
    items: [
      { q: "Does WebsiteTech guarantee advertising results?",
        a: "No. Advertising results vary substantially based on factors including business and industry, product or service, offer, audience, location, competition, advertising budget, creative quality, advertising platform, campaign timing, landing-page experience, website performance, and market conditions.\n\nWebsiteTech uses AI, campaign analytics and available performance information to help customers make better-informed campaign decisions, but it cannot guarantee clicks, leads, sales, revenue or return on advertising spend." },
      { q: "How quickly should I expect results?",
        a: "There is no universal timeframe. Some campaigns receive responses quickly; others require more time and testing. WebsiteTech evaluates campaigns based on sufficient data rather than making premature conclusions from a small number of impressions or clicks." },
      { q: "Will WebsiteTech automatically improve a poorly performing campaign?",
        a: "WebsiteTech identifies potential problems and recommends changes. For example:\n\n\"This creative appears to be underperforming compared with the other campaign creatives. Would you like WebsiteTech to generate three alternatives?\"\n\nYou review the proposed changes before they are deployed." },
      { q: "What if people click my advertisement but don't become customers?",
        a: "Campaign Intelligence helps investigate the entire customer journey. The problem may not be the advertisement. WebsiteTech examines available indicators involving:\n\nAdvertisement → Landing Page → CTA → Form → Lead → Conversion\n\nIt may recommend improvements to the ad, offer, landing page, mobile experience, form or other parts of the customer journey." },
    ],
  },
  {
    id: "why",
    title: "Why WebsiteTech",
    icon: HelpCircle,
    items: [
      { q: "How is WebsiteTech different from a normal AI image generator?",
        a: "An AI image generator primarily creates images. WebsiteTech is designed around the complete campaign lifecycle:\n\nBusiness Understanding → Brand Brain → Audience Brain → Campaign Strategy → Copy + Visual Creation → Multiple Advertising Formats → Human Approval → Splink.pro + QR Tracking → Campaign Distribution → Leads + Conversions → Campaign Intelligence → Better-Informed Future Campaigns" },
      { q: "How is WebsiteTech different from a traditional advertising agency?",
        a: "WebsiteTech automates many repetitive campaign-production and analysis tasks using AI while keeping important business decisions under human control. Businesses may use WebsiteTech independently, with their own marketing team, or alongside an advertising agency. The objective is not to eliminate professional marketing expertise — it's to make professional campaign tools more accessible, measurable and efficient." },
      { q: "What is the long-term goal of WebsiteTech Campaign Studio?",
        a: "The goal is to make sophisticated advertising technology accessible to businesses that may not have dedicated marketing departments. A business owner should eventually be able to say:\n\n\"Create a campaign for my business.\"\n\nWebsiteTech will understand the brand and audience, examine previous campaign information, create professional advertising options, prepare the required formats, generate tracking links and QR codes, obtain approval, distribute through authorized channels, measure available results, and use those results to improve future recommendations.\n\nCreate → Run → Measure → Learn → Improve." },
    ],
  },
];
